// Authentication service
// Handles login/logout API calls

import type { LoginCredentials, LoginResponse, User, UserRoleType } from './types';
import { UserRole } from './types';
import type { ApiError } from '../errors/types';

// API configuration - uses env variable, falls back to production URL
const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is not defined');
}

// Token storage keys — single source of truth, imported by other services
export const ACCESS_TOKEN_KEY = 'startus_access_token';
export const REFRESH_TOKEN_KEY = 'startus_refresh_token';

// TODO(safari-itp): Temporary workaround for Safari/iOS WebKit ITP blocking cross-domain httpOnly cookies.
// Once FE and API are on the same parent domain (e.g. *.startus.com) with COOKIE_DOMAIN=.startus.com,
// remove these helpers and revert validateToken, login, _doRefresh, logout to cookie-only auth.
const SESSION_TOKEN_KEY = 'startus_session_token';
const SESSION_REFRESH_KEY = 'startus_session_refresh';
export const storeAccessToken = (token: string): void => {
  try {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    /* storage unavailable */
  }
};
export const getStoredAccessToken = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const clearStoredAccessToken = (): void => {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};
export const storeRefreshToken = (token: string): void => {
  try {
    sessionStorage.setItem(SESSION_REFRESH_KEY, token);
  } catch {
    /* storage unavailable */
  }
};
export const getStoredRefreshToken = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_REFRESH_KEY);
  } catch {
    return null;
  }
};
export const clearStoredRefreshToken = (): void => {
  try {
    sessionStorage.removeItem(SESSION_REFRESH_KEY);
  } catch {
    /* ignore */
  }
};

// Backend user shape
interface BackendUser {
  id: string;
  email: string;
  name: string | null;
  lastname: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  role?: string;
  hasAccess?: boolean;
  mustChangePassword?: boolean;
  socialMedia?: { url: string }[];
  // Only present on /me (login's response omits it) — fine since AuthProvider
  // always refetches /me right after login to populate the full user.
  hubMemberships?: { investorId: string; role: string }[];
}

// Backend login response
interface LoginApiResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    user: BackendUser;
  };
  code?: string;
  message?: string;
  error?: string;
}

const KNOWN_ROLES: readonly string[] = Object.values(UserRole);

// backendUser.role is whatever the API sends on `/auth/me` — validate against the
// known enum values instead of trusting it blindly, and fall back to the least
// privileged role if it's ever missing or unrecognized (e.g. an older API version).
function toUserRole(role: string | undefined): UserRoleType {
  return role && KNOWN_ROLES.includes(role) ? (role as UserRoleType) : UserRole.USER;
}

// Maps backend user to frontend User type
function mapBackendUser(backendUser: BackendUser): User {
  return {
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.name || '',
    lastname: backendUser.lastname || '',
    avatarUrl: backendUser.avatarUrl,
    isActive: backendUser.isActive,
    role: toUserRole(backendUser.role),
    socialMedia: backendUser.socialMedia || [],
    hasAccess: backendUser.hasAccess ?? false,
    mustChangePassword: backendUser.mustChangePassword ?? false,
    hubMemberships: backendUser.hubMemberships ?? [],
  };
}

// Login with real API — sets httpOnly cookie (BE) + stores access token for Bearer auth (TODO: remove when same-domain)
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: credentials.email,
      password: credentials.password,
      ...(credentials.source && { source: credentials.source }),
    }),
    credentials: 'include',
  });

  const result: LoginApiResponse = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    throw {
      status: response.status,
      code: result?.code,
      message: result?.message || result?.error || 'Invalid email or password',
    } satisfies ApiError;
  }

  storeAccessToken(result.data.accessToken); // TODO(safari-itp): remove when same-domain
  storeRefreshToken(result.data.refreshToken); // TODO(safari-itp): remove when same-domain
  return { user: mapBackendUser(result.data.user) };
};

// Logout — cookie cleared by BE, sessionStorage token cleared locally.
// Awaits the server call so callers know the session is invalidated before proceeding.
// Network errors are swallowed — local tokens are cleared regardless.
export const logout = async (): Promise<void> => {
  clearStoredAccessToken(); // TODO(safari-itp): remove when same-domain
  clearStoredRefreshToken(); // TODO(safari-itp): remove when same-domain
  await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {});
};

// No-op — kept for backward compat; auth is cookie-only
export const getStoredToken = (): string | null => null;

// Backend /me response shape
interface MeApiResponse {
  success: boolean;
  data: BackendUser;
  message?: string;
  error?: string;
}

// Backend refresh token response shape
interface RefreshTokenApiResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken?: string;
  };
  message?: string;
  error?: string;
}

// Singleton in-flight refresh promise — prevents concurrent callers from each triggering
// a separate refresh, which would invalidate a just-rotated refresh token.
let _refreshPromise: Promise<string | null> | null = null;

async function _doRefresh(): Promise<string | null> {
  try {
    // TODO(safari-itp): remove storedRefreshToken body param when same-domain (cookie will be sufficient)
    const storedRefreshToken = getStoredRefreshToken();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      ...(storedRefreshToken && {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      }),
    });
    const result: RefreshTokenApiResponse = await response.json().catch(() => null);
    if (!response.ok || !result?.success) return null;
    storeAccessToken(result.data.accessToken); // TODO(safari-itp): remove when same-domain
    if (result.data.refreshToken) storeRefreshToken(result.data.refreshToken); // TODO(safari-itp)
    return result.data.accessToken;
  } catch {
    return null;
  }
}

// Refresh access token via cookie — BE rotates the httpOnly cookie.
// Returns the new access token from the response body for in-memory use (e.g. WebSocket auth).
// Does NOT store the token anywhere — cookie is the only persistent storage.
// Concurrent calls share a single in-flight request.
export const refreshAccessToken = (): Promise<string | null> => {
  if (!_refreshPromise) {
    _refreshPromise = _doRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
};

// Validate session — prefers sessionStorage Bearer token (Safari/iOS ITP workaround) over cookie.
// TODO(safari-itp): when same-domain, remove Bearer header logic and revert to cookie-only.
export const validateToken = async (): Promise<User | null> => {
  try {
    const storedToken = getStoredAccessToken();
    const headers: HeadersInit = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};

    const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    const result: MeApiResponse = await response.json().catch(() => null);

    if (response.ok && result?.success) return mapBackendUser(result.data);

    if (response.status === 401 || response.status === 403) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) return null;

      const retryResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: { Authorization: `Bearer ${refreshed}` },
      });
      const retryResult: MeApiResponse = await retryResponse.json().catch(() => null);
      if (retryResponse.ok && retryResult?.success) return mapBackendUser(retryResult.data);
    }

    return null;
  } catch {
    return null;
  }
};

// Check whether an email is registered — used to gate the magic-link flow (web only)
export const checkEmailRegistered = async (email: string): Promise<boolean> => {
  const encoded = encodeURIComponent(email);
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/check-email/${encoded}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    throw {
      status: response.status,
      code: result?.code,
      message: result?.message || 'Failed to check email',
    } satisfies ApiError;
  }
  const result = await response.json();
  return result?.data?.registered === true;
};

// Send 6-digit login code to email
export const sendLoginCode = async (email: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/send-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    credentials: 'include',
  });
  if (!response.ok) {
    const result = await response.json().catch(() => null);
    throw {
      status: response.status,
      code: result?.code,
      message: result?.message || 'Failed to send code',
    } satisfies ApiError;
  }
};

// Verify 6-digit code — BE sets httpOnly cookie; also stores token for Safari/iOS ITP compat
// TODO(safari-itp): when same-domain, drop token extraction and revert to checking response.ok only
export const verifyLoginCode = async (email: string, code: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
    credentials: 'include',
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw {
      status: response.status,
      code: result?.code,
      message: result?.message || 'Invalid or expired code',
    } satisfies ApiError;
  }
  if (result?.data?.accessToken) {
    storeAccessToken(result.data.accessToken);
    if (result.data.refreshToken) storeRefreshToken(result.data.refreshToken); // TODO(safari-itp)
  }
};
